import * as XLSX from 'xlsx'

const HEADER_MAP = {
    beneficiary: 'beneficiary',
    salenumber: 'saleNumber',
    paydate: 'payDate',
    referencenumber: 'referenceNumber',
    paidamount: 'paidAmount',
    bank: 'bank',
}

function normalizeHeaderKey(key) {
    return key.trim().toLowerCase().replace(/\s+/g, '')
}

function normalizeRows(rows) {
    return rows.map(row => {
        const out = {}
        Object.keys(row).forEach(key => {
            const normalized = normalizeHeaderKey(key)
            out[HEADER_MAP[normalized] || normalized] = row[key]
        })
        return out
    })
}

function toISODateString(value) {
    if (value === null || value === undefined || value === '') return ''
    if (value instanceof Date && !Number.isNaN(value.getTime())) {
        return value.toISOString().slice(0, 10)
    }
    const parsed = new Date(value)
    return Number.isNaN(parsed.getTime()) ? '' : parsed.toISOString().slice(0, 10)
}

// Reads the first sheet of an uploaded workbook and returns normalized row
// objects (keyed by saleNumber/payDate/referenceNumber/paidAmount/bank).
export function parseProofWorkbook(file) {
    return new Promise((resolve, reject) => {
        const reader = new FileReader()
        reader.onload = evt => {
            try {
                const data = new Uint8Array(evt.target.result)
                const workbook = XLSX.read(data, { type: 'array', cellDates: true })
                const sheet = workbook.Sheets[workbook.SheetNames[0]]
                const rows = XLSX.utils.sheet_to_json(sheet, { defval: '', raw: true })
                resolve(normalizeRows(rows))
            } catch (err) {
                reject(err)
            }
        }
        reader.onerror = () => reject(reader.error)
        reader.readAsArrayBuffer(file)
    })
}

// Matches each imported row against the payments currently awaiting proof and
// flags anything that can't be submitted as-is (missing/invalid fields,
// unknown sale number, duplicate row, unrecognized bank).
export function buildPreviewRows(rawRows, pendingPayments, banks) {
    const claimedIds = new Set()

    return rawRows.map((raw, index) => {
        const beneficiaryName = String(raw.beneficiary ?? '').trim()
        const saleNumber = String(raw.saleNumber ?? '').trim()
        const payDate = toISODateString(raw.payDate)
        const referenceNumber = String(raw.referenceNumber ?? '').trim()
        const paidAmountRaw = raw.paidAmount
        const paidAmount = paidAmountRaw !== '' && paidAmountRaw !== null && paidAmountRaw !== undefined
            ? Number(paidAmountRaw) : null
        const bankName = String(raw.bank ?? '').trim()

        const matched = saleNumber
            ? pendingPayments.find(p => p.saleNumber.toLowerCase() === saleNumber.toLowerCase())
            : null

        const matchedBank = bankName
            ? banks.find(b => b.name.toLowerCase() === bankName.toLowerCase())
            : null

        const errors = []
        if (!saleNumber) errors.push('Missing sale number')
        else if (!matched) errors.push('Sale number not found among payments awaiting proof')
        else if (claimedIds.has(matched.id)) errors.push('Duplicate row for this sale number')

        if (!payDate) errors.push('Missing or invalid pay date')
        if (!referenceNumber) errors.push('Missing reference number')
        if (paidAmount === null || Number.isNaN(paidAmount)) errors.push('Missing or invalid paid amount')
        if (!bankName) errors.push('Missing bank')
        else if (!matchedBank) errors.push(`Bank "${bankName}" not recognized`)

        if (matched && beneficiaryName) {
            const typed = beneficiaryName.toLowerCase()
            const actual = matched.beneficiary.name.toLowerCase()
            if (!actual.includes(typed) && !typed.includes(actual)) {
                errors.push('Beneficiary name does not match this sale number')
            }
        }

        if (matched && errors.length === 0) claimedIds.add(matched.id)

        return {
            key: `row-${index}-${saleNumber || index}`,
            beneficiaryName,
            saleNumber,
            payDate,
            referenceNumber,
            paidAmount,
            bankName,
            bankId: matchedBank ? matchedBank.id : null,
            matched,
            errors,
        }
    })
}

// Builds and downloads the .xlsx template account officers fill in and
// re-upload. Uses real sale numbers from the current pending list as examples
// when available, so the format is unambiguous.
export function downloadProofTemplate(pendingPayments = [], banks = []) {
    const headers = ['Beneficiary', 'Sale Number', 'Pay Date', 'Reference Number', 'Paid Amount', 'Bank']
    const defaultBankName = banks.length ? banks[0].name : 'CRDB Bank'

    const exampleRows = pendingPayments.length
        ? pendingPayments.slice(0, 3).map(p => [p.beneficiary.name, p.saleNumber, '', '', p.amount.toFixed(3), defaultBankName])
        : [['Juma Mwakimbe', 'SN20260001', '', '', '1250.000', defaultBankName]]

    const sheet = XLSX.utils.aoa_to_sheet([headers, ...exampleRows])
    sheet['!cols'] = [{ wch: 22 }, { wch: 18 }, { wch: 14 }, { wch: 22 }, { wch: 14 }, { wch: 16 }]

    const bankNames = banks.length ? banks.map(b => b.name).join(', ') : 'CRDB Bank, Azania Bank'
    const instructions = XLSX.utils.aoa_to_sheet([
        ['Column', 'Required', 'Notes'],
        ['Beneficiary', 'No', 'Informational only — matching uses Sale Number. If provided, it is checked against the sale\'s actual beneficiary and flagged if it doesn\'t match.'],
        ['Sale Number', 'Yes', 'Must exactly match a Sale # from the "Awaiting proof" list.'],
        ['Pay Date', 'Yes', 'Format YYYY-MM-DD, e.g. 2026-08-10.'],
        ['Reference Number', 'Yes', 'The EDMS reference recorded for the bank transfer.'],
        ['Paid Amount', 'Yes', 'The amount actually paid, in USD.'],
        ['Bank', 'Yes', `Must match a known bank exactly: ${bankNames}.`],
        [],
        ['Sample rows on the first sheet use real beneficiaries/sale numbers currently awaiting proof —'],
        ['replace Pay Date, Reference Number, Paid Amount and Bank with the real values, or delete rows that do not apply.'],
    ])
    instructions['!cols'] = [{ wch: 18 }, { wch: 10 }, { wch: 70 }]

    const workbook = XLSX.utils.book_new()
    XLSX.utils.book_append_sheet(workbook, sheet, 'Proof of Payment')
    XLSX.utils.book_append_sheet(workbook, instructions, 'Instructions')
    XLSX.writeFile(workbook, 'proof_of_payment_template.xlsx')
}
